import axios from 'axios'
import livableCitySupplement from '@/data/livableCitySupplement.json'

const API_BASE_URL = 'https://api.traveoic.com'
const API_TIMEOUT = 10000
const DEFAULT_BATCH = '1'
const CACHE_TTL = 1000 * 60 * 60
const CACHE_VERSION = '2026-07-08-metrics-by-id-v1'
const FALLBACK_IMAGE = 'https://picsum.photos/seed/livable-city/1200/800'
const REGION_ORDER = ['Europe', 'Asia', 'Americas', 'Oceania']
const MATCHER_OPTIONS = {
	vibe: ['Urban Pulse', 'Balanced Serenity', 'Cultural Soul'],
	climate: ['Eternal Summer', 'Four Seasons', 'Cool & Crisp'],
	budget: ['Global High-End', 'Smart Value', 'Affordable Gem'],
}
const STANDARD_OPTIONS = [
	{
		key: 'airQuality',
		label: 'Air Quality',
		apiLabel: 'Air Quality',
		topValue: 'Excellent',
		levelFields: ['airQuality', 'air_quality', 'airLevel', 'air_level', 'airQualityLevel', 'air_quality_level'],
		descriptionFields: ['airDesc', 'air_desc', 'airQualityDesc', 'air_quality_desc', 'airDescription', 'air_quality_description'],
	},
	{
		key: 'safety',
		label: 'Safety',
		apiLabel: 'Safety',
		topValue: 'Very High',
		levelFields: ['safety', 'safetyLevel', 'safety_level'],
		descriptionFields: ['safetyDesc', 'safety_desc', 'safetyDescription', 'safety_description'],
	},
	{
		key: 'livingCost',
		label: 'Living Cost',
		apiLabel: 'Living Cost',
		topValue: 'Low',
		levelFields: ['livingCost', 'living_cost', 'costLevel', 'cost_level', 'livingCostLevel', 'living_cost_level'],
		descriptionFields: ['costDesc', 'cost_desc', 'livingCostDesc', 'living_cost_desc', 'costDescription', 'living_cost_description'],
	},
	{
		key: 'healthcare',
		label: 'Healthcare',
		apiLabel: 'Healthcare',
		topValue: 'Excellent',
		levelFields: ['healthcare', 'healthcareLevel', 'healthcare_level', 'healthLevel', 'health_level'],
		descriptionFields: ['healthcareDesc', 'healthcare_desc', 'healthDesc', 'health_desc', 'healthcareDescription', 'health_description'],
	},
	{
		key: 'transport',
		label: 'Transport',
		apiLabel: 'Transport',
		topValue: 'Excellent',
		levelFields: ['transport', 'transportLevel', 'transport_level', 'transLevel', 'trans_level'],
		descriptionFields: ['transportDesc', 'transport_desc', 'transDesc', 'trans_desc', 'transportDescription', 'trans_description'],
	},
]

let cache = { data: null, expiresAt: 0 }
const BLOG_CACHE_KEY = 'livableCityCache'

async function fetchPublicBlogs() {
	if (typeof window === 'undefined') {
		try {
			const fs = require('fs')
			const path = require('path')
			const filePath = path.resolve(process.cwd(), 'public', 'blogs.json')
			if (!fs.existsSync(filePath)) {
				return null
			}
			const raw = fs.readFileSync(filePath, 'utf-8')
			const list = JSON.parse(raw)
			return Array.isArray(list) ? list : null
		} catch (error) {
			return null
		}
	}
	try {
		const response = await axios.get('/blogs.json', { timeout: 5000 })
		const list = response?.data
		return Array.isArray(list) ? list : null
	} catch (error) {
		return null
	}
}

function clone(data) {
	return JSON.parse(JSON.stringify(data))
}

function hasValidCache() {
	return cache.data && Date.now() < cache.expiresAt
}

function normalizeStorageCache(parsed) {
	if (!parsed || typeof parsed !== 'object') {
		return null
	}
	// 兼容旧格式（直接写 payload）与新格式（{ savedAt, data }）
	if (parsed.data && typeof parsed.data === 'object') {
		return {
			data: parsed.data,
			savedAt: Number(parsed.savedAt) || 0,
			version: String(parsed.version || ''),
		}
	}
	return {
		data: parsed,
		savedAt: 0,
		version: '',
	}
}

function getEmptyPayload() {
	return {
		cities: [],
		blogs: [],
		quotes: [],
		homeHeroSlides: [],
		homeRegionCards: [],
		homeMatcherOptions: MATCHER_OPTIONS,
		regionOptions: [],
		standardOptions: STANDARD_OPTIONS.map(({ key, label }) => ({ key, label })),
		topStandardValue: getTopStandardValue(),
	}
}

function getUsableStorageCache(raw) {
	const parsed = normalizeStorageCache(JSON.parse(raw))
	if (!parsed || parsed.version !== CACHE_VERSION) {
		return null
	}
	if (parsed.savedAt && Date.now() - parsed.savedAt > CACHE_TTL) {
		return null
	}
	return parsed.data
}

function readServerCacheFromStorage() {
	try {
		const fs = require('fs')
		const path = require('path')
		const filePath = path.resolve(process.cwd(), 'tmp', 'livableCityCache.json')
		if (!fs.existsSync(filePath)) {
			return null
		}
		return getUsableStorageCache(fs.readFileSync(filePath, 'utf-8'))
	} catch (error) {
		return null
	}
}

function readBrowserCacheFromStorage() {
	try {
		const raw = window.localStorage.getItem(BLOG_CACHE_KEY)
		if (!raw) {
			return null
		}
		return getUsableStorageCache(raw)
	} catch (error) {
		return null
	}
}

function readCacheFromStorage() {
	return typeof window === 'undefined' ? readServerCacheFromStorage() : readBrowserCacheFromStorage()
}

function writeCacheToStorage(payload) {
	if (!payload || typeof payload !== 'object') {
		return
	}
	const wrappedPayload = {
		savedAt: Date.now(),
		version: CACHE_VERSION,
		data: payload,
	}
	if (typeof window === 'undefined') {
		try {
			const fs = require('fs')
			const path = require('path')
			const dirPath = path.resolve(process.cwd(), 'tmp')
			if (!fs.existsSync(dirPath)) {
				fs.mkdirSync(dirPath, { recursive: true })
			}
			const filePath = path.resolve(dirPath, 'livableCityCache.json')
			fs.writeFileSync(filePath, JSON.stringify(wrappedPayload))
		} catch (error) {
			// ignore
		}
		return
	}
	try {
		window.localStorage.setItem(BLOG_CACHE_KEY, JSON.stringify(wrappedPayload))
	} catch (error) {
		// ignore
	}
}

function slugifyCityName(name) {
	return String(name || '')
		.toLowerCase()
		.replace(/\./g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
}

function normalizeRegion(region) {
	const raw = String(region || '').trim()
	if (!raw) {
		return 'Asia'
	}
	if (raw === 'America') {
		return 'Americas'
	}
	return raw
}

function toScore(score) {
	const parsed = Number(String(score || '').trim())
	if (Number.isNaN(parsed)) {
		return 0
	}
	return Number(parsed.toFixed(1))
}

function hashString(value) {
	return String(value || '').split('').reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % 100000, 7)
}

function normalizeCity(rawCity = {}) {
	const slug = slugifyCityName(rawCity.name)
	return {
		id: rawCity.id,
		name: rawCity.name || 'Unknown City',
		slug: slug || `city-${rawCity.id || hashString(JSON.stringify(rawCity))}`,
		country: rawCity.country || 'Unknown',
		region: normalizeRegion(rawCity.region),
		language: rawCity.language || 'English',
		score: toScore(rawCity.score),
		image: rawCity.img || FALLBACK_IMAGE,
	}
}

function createCityFit(city) {
	const seed = hashString(city.slug)
	return {
		vibe: MATCHER_OPTIONS.vibe[seed % MATCHER_OPTIONS.vibe.length],
		climate: MATCHER_OPTIONS.climate[(seed + 1) % MATCHER_OPTIONS.climate.length],
		budget: MATCHER_OPTIONS.budget[(seed + 2) % MATCHER_OPTIONS.budget.length],
	}
}

function extractApiData(response) {
	if (response?.data?.code === 200) {
		return response.data.data
	}
	return null
}

function getTopStandardValue() {
	return STANDARD_OPTIONS.reduce((acc, item) => {
		acc[item.key] = item.topValue
		return acc
	}, {})
}

function buildMetricMembership(targetData = {}) {
	const memberMap = {}
	STANDARD_OPTIONS.forEach((item) => {
		const list = targetData[item.apiLabel] || []
		memberMap[item.key] = new Set(list.map((city) => String(city.id || '')))
	})
	return memberMap
}

function getSupplementCityId(rawCity = {}, city = {}) {
	const cityId = String(rawCity.id || city.id || '')
	if (cityId && livableCitySupplement.metricsByCityId?.[cityId]) {
		return cityId
	}
	const cityName = cleanText(rawCity.name || city.name).toLowerCase()
	const matchedCity = (livableCitySupplement.cities || []).find((item) => {
		return cleanText(item.name).toLowerCase() === cityName
	})
	return matchedCity ? String(matchedCity.id) : cityId
}

function getSupplementMetric(rawCity, city, key) {
	const cityId = getSupplementCityId(rawCity, city)
	return livableCitySupplement.metricsByCityId?.[cityId]?.[key] || {}
}

function getSupplementQuotes(rawCity, city) {
	const cityId = getSupplementCityId(rawCity, city)
	return (livableCitySupplement.quotesByCityId?.[cityId] || []).map((item) => ({
		id: item.id || hashString(`${cityId}-${item.content}`),
		text: cleanText(item.content),
		author: cleanText(item.author),
	})).filter((item) => item.text)
}

function cleanText(value) {
	return String(value || '').trim()
}

function getObjectMetricValue(source, fieldName) {
	if (!source || typeof source !== 'object') {
		return ''
	}
	const aliases = fieldName === 'level'
		? ['value', 'level', 'name', 'label']
		: ['description', 'desc', 'body', 'content']
	for (const key of aliases) {
		if (source[key]) {
			return source[key]
		}
	}
	return ''
}

function getNestedMetricValue(rawCity, item, fieldName) {
	const nestedSources = [
		rawCity?.metrics?.[item.key],
		rawCity?.metricDetails?.[item.key],
		rawCity?.cityMetrics?.[item.key],
		rawCity?.city_metrics?.[item.key],
	]
	for (const source of nestedSources) {
		if (!source) {
			continue
		}
		if (typeof source === 'string' && fieldName === 'level') {
			return source
		}
		const objectValue = getObjectMetricValue(source, fieldName)
		if (objectValue) {
			return objectValue
		}
	}
	return ''
}

function getMetricApiValue(rawCity, item, fieldName) {
	const fields = fieldName === 'level' ? item.levelFields : item.descriptionFields
	for (const field of fields) {
		if (rawCity && rawCity[field]) {
			return cleanText(rawCity[field])
		}
	}
	return cleanText(getNestedMetricValue(rawCity, item, fieldName))
}

function buildMetricDetail(rawCity, city, item, membershipMap, detailMetrics) {
	const detailValue = getMetricApiValue(detailMetrics, item, 'level')
	const detailDescription = getMetricApiValue(detailMetrics, item, 'description')
	const apiValue = detailValue || getMetricApiValue(rawCity, item, 'level')
	const apiDescription = detailDescription || getMetricApiValue(rawCity, item, 'description')
	const supplementMetric = getSupplementMetric(rawCity, city, item.key)
	if (apiValue || apiDescription) {
		return {
			value: apiValue || cleanText(supplementMetric.value),
			description: apiDescription || cleanText(supplementMetric.description),
			source: detailValue || detailDescription ? 'metricsByIdApi' : 'cityMetricsApi',
		}
	}
	const inTopGroup = membershipMap[item.key]?.has(String(city.id || ''))
	return {
		value: inTopGroup ? item.topValue : cleanText(supplementMetric.value),
		description: cleanText(supplementMetric.description),
		source: inTopGroup ? 'cityByTarget+sheetSupplement' : 'sheetSupplement',
	}
}

function createMetricDetails(rawCity, city, membershipMap, detailMetrics) {
	return STANDARD_OPTIONS.reduce((acc, item) => {
		acc[item.key] = buildMetricDetail(rawCity, city, item, membershipMap, detailMetrics)
		return acc
	}, {})
}

function createMetrics(metricDetails) {
	return STANDARD_OPTIONS.reduce((acc, item) => {
		acc[item.key] = metricDetails[item.key]?.value || ''
		return acc
	}, {})
}

function collectCities(apiData = {}) {
	const all = []
	const pushList = (list = []) => list.forEach((city) => all.push(city))
	pushList(apiData.randomCity)
	pushList(apiData.selectCity)
	pushList(apiData.citySet1)
	pushList(apiData.citySet2)
	pushList(apiData.citySet3)
	pushList((apiData.randomQuotes || []).map((item) => item.city).filter(Boolean))
	Object.values(apiData.cityByTarget || {}).forEach((list) => pushList(list))
	pushList(livableCitySupplement.cities || [])
	const seen = new Set()
	return all.filter((city) => {
		const key = String(city.id || '') || slugifyCityName(city.name)
		if (!key || seen.has(key)) {
			return false
		}
		seen.add(key)
		return true
	})
}

function buildRegionCards(cities = []) {
	return REGION_ORDER.map((region) => {
		const sample = cities.find((city) => city.region === region)
		return {
			key: region,
			name: region,
			image: sample?.image || FALLBACK_IMAGE,
		}
	})
}

function buildQuotes(randomQuotes = []) {
	return randomQuotes.map((item) => ({
		id: item?.quotes?.id || hashString(item?.quotes?.content || ''),
		text: cleanText(item?.quotes?.content),
		author: cleanText(item?.quotes?.author),
		image: item?.city?.img || FALLBACK_IMAGE,
	})).filter((item) => item.text)
}

function normalizeCityQuotes(cityQuotes = []) {
	return cityQuotes.map((item) => ({
		id: item?.id || hashString(item?.content || ''),
		text: cleanText(item?.content),
		author: cleanText(item?.author),
	})).filter((item) => item.text)
}

function getCityId(rawCity = {}) {
	return String(rawCity?.id || rawCity?.cityId || '').trim()
}

function normalizeCityDetailQuotes(detail) {
	return normalizeCityQuotes(detail?.quotes || [])
}

function getCityDetail(rawCity, apiData = {}) {
	const cityId = getCityId(rawCity)
	return apiData.cityDetailsById?.[cityId] || null
}

function mergeDetailCity(rawCity, detail) {
	if (!detail?.city || typeof detail.city !== 'object') {
		return rawCity
	}
	return {
		...rawCity,
		...detail.city,
		id: rawCity?.id || detail.city.id,
	}
}

function buildCityPayload(raw, apiData, membershipMap) {
	const detail = getCityDetail(raw, apiData)
	const mergedRaw = mergeDetailCity(raw, detail)
	const city = normalizeCity(mergedRaw)
	const cityId = String(city.id || '')
	const metricDetails = createMetricDetails(mergedRaw, city, membershipMap, detail?.metrics)
	const detailQuotes = normalizeCityDetailQuotes(detail)
	const cityQuotes = apiData.cityQuotesById?.[cityId] || []
	const quoteSource = detailQuotes.length ? detailQuotes : cityQuotes.length ? cityQuotes : getSupplementQuotes(mergedRaw, city)
	return {
		...city,
		metrics: createMetrics(metricDetails),
		metricDetails,
		fit: createCityFit(city),
		cityQuotes: quoteSource.slice(0, 3),
	}
}

function buildPayloadFromApi(apiData, fallbackPayload) {
	const membershipMap = buildMetricMembership(apiData.cityByTarget)
	const topStandardValue = getTopStandardValue()
	const quotes = buildQuotes(apiData.randomQuotes)
	const cities = collectCities(apiData).map((raw) => buildCityPayload(raw, apiData, membershipMap))
	const regionOptions = REGION_ORDER.filter((region) => cities.some((city) => city.region === region))
	return {
		cities: cities.length ? cities : fallbackPayload.cities,
		blogs: fallbackPayload.blogs,
		quotes: quotes.length ? quotes : fallbackPayload.quotes,
		homeHeroSlides: fallbackPayload.homeHeroSlides,
		homeRegionCards: buildRegionCards(cities.length ? cities : fallbackPayload.cities),
		homeMatcherOptions: MATCHER_OPTIONS,
		regionOptions: regionOptions.length ? regionOptions : fallbackPayload.regionOptions,
		standardOptions: STANDARD_OPTIONS.map(({ key, label }) => ({ key, label })),
		topStandardValue,
	}
}

async function requestCityQuotes(cityId) {
	if (!cityId) {
		return []
	}
	const data = await requestCityApi('/city/quotesByCity', { cityId })
	return Array.isArray(data) ? normalizeCityQuotes(data) : []
}

async function requestCityDetail(cityId) {
	if (!cityId) {
		return null
	}
	return requestCityApi('/city/metricsById', { id: cityId })
}

async function buildCityDetailsMap(rawCities = []) {
	const cityIds = Array.from(new Set(rawCities.map((city) => getCityId(city)).filter(Boolean)))
	const detailMap = {}
	const chunkSize = 6
	for (let index = 0; index < cityIds.length; index += chunkSize) {
		const chunk = cityIds.slice(index, index + chunkSize)
		const entries = await Promise.all(chunk.map(async (cityId) => [cityId, await requestCityDetail(cityId)]))
		entries.forEach(([cityId, detail]) => {
			if (detail && typeof detail === 'object') {
				detailMap[cityId] = detail
			}
		})
	}
	return detailMap
}

async function buildCityQuotesMap(rawCities = [], cityDetailsById = {}) {
	const cityIds = Array.from(new Set(rawCities.map((city) => getCityId(city)).filter(Boolean)))
	const quoteMap = {}
	cityIds.forEach((cityId) => {
		const detailQuotes = normalizeCityDetailQuotes(cityDetailsById[cityId])
		if (detailQuotes.length) {
			quoteMap[cityId] = detailQuotes
		}
	})
	const cityIdsNeedingQuotes = cityIds.filter((cityId) => !quoteMap[cityId]?.length)
	const chunkSize = 8
	for (let index = 0; index < cityIdsNeedingQuotes.length; index += chunkSize) {
		const chunk = cityIdsNeedingQuotes.slice(index, index + chunkSize)
		const entries = await Promise.all(chunk.map(async (cityId) => [cityId, await requestCityQuotes(cityId)]))
		entries.forEach(([cityId, quotes]) => {
			quoteMap[cityId] = quotes
		})
	}
	return quoteMap
}

async function requestCityApi(path, params = {}) {
	try {
		const response = await axios.get(`${API_BASE_URL}${path}`, {
			params: {
				batch: DEFAULT_BATCH,
				...params,
			},
			timeout: API_TIMEOUT,
		})
		return extractApiData(response)
	} catch (error) {
		console.error(`[livableCityService] request failed: ${path}`, error)
		return null
	}
}

async function requestBaseCityData() {
	const [randomQuotes, randomCity, cityByTarget, selectCity, citySet1, citySet2, citySet3] = await Promise.all([
		requestCityApi('/city/randomQuotes/v2'),
		requestCityApi('/city/randomCity'),
		requestCityApi('/city/cityByTarget'),
		requestCityApi('/city/selectCity', {
			continent: 'Asia,Europe,Americas,Oceania',
		}),
		requestCityApi('/city/citySet1'),
		requestCityApi('/city/citySet2'),
		requestCityApi('/city/citySet3'),
	])
	return {
		randomQuotes: randomQuotes || [],
		randomCity: randomCity || [],
		cityByTarget: cityByTarget || {},
		selectCity: selectCity || [],
		citySet1: citySet1 || [],
		citySet2: citySet2 || [],
		citySet3: citySet3 || [],
	}
}

async function buildFreshPayload(fallbackPayload) {
	const apiData = await requestBaseCityData()
	const rawCities = collectCities(apiData)
	if (!rawCities.length) {
		return { payload: null, hasApiData: false }
	}
	apiData.cityDetailsById = await buildCityDetailsMap(rawCities)
	apiData.cityQuotesById = await buildCityQuotesMap(rawCities, apiData.cityDetailsById)
	return {
		payload: buildPayloadFromApi(apiData, fallbackPayload),
		hasApiData: true,
	}
}

function applyPublicBlogs(payload, publicBlogs) {
	if (publicBlogs && publicBlogs.length) {
		payload.blogs = publicBlogs
	}
	return payload
}

async function fetchLivableCityData() {
	const fallbackPayload = getEmptyPayload()

	if (hasValidCache()) {
		return clone(cache.data)
	}

	const freshStorageCache = readCacheFromStorage()
	if (freshStorageCache) {
		cache = { data: freshStorageCache, expiresAt: Date.now() + CACHE_TTL }
		return clone(freshStorageCache)
	}

	const [publicBlogs, freshResult] = await Promise.all([
		fetchPublicBlogs(),
		buildFreshPayload(fallbackPayload),
	])
	const hasApiData = freshResult.hasApiData
	let payload = freshResult.payload || fallbackPayload

	payload = applyPublicBlogs(payload, publicBlogs)
	if (hasApiData) {
		writeCacheToStorage(payload)
	}
	cache = { data: payload, expiresAt: Date.now() + CACHE_TTL }
	return clone(payload)
}

export { fetchLivableCityData }
